import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterOutlet } from '@angular/router';

import {
  Observable,
  Subject,
  catchError,
  combineLatest,
  concatMap,
  filter,
  map,
  of,
  take,
  tap,
  throwError
} from 'rxjs';

import { STORAGE_KEYS } from '@core/constants/storage-keys';
import { Apps } from '@core/models/app-switcher.state';
import { LicenseStatus } from '@core/models/portal.state';
import { AppSwitcherService } from '@core/services/app-switcher.service';
import { AuthService } from '@core/services/auth.service';
import { CompanyApiService } from '@core/services/company-api.service';
import { CurrentUserService } from '@core/services/current-user.service';
import { DevAuthService } from '@core/services/dev-auth.service';

import { IconComponent } from '@shared/components/icon/icon.component';

import { NewDonationService } from '@donation/new-donation/services/new-donation.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, IconComponent, MatButtonModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {
  protected readonly LicenseStatus = LicenseStatus;

  private readonly authService = inject(AuthService);
  private readonly devAuth = inject(DevAuthService);
  private readonly appSwitcher = inject(AppSwitcherService);
  private readonly companyApi = inject(CompanyApiService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly newDonationSvc = inject(NewDonationService);
  private readonly router = inject(Router);

  readonly licenseStatus = signal<LicenseStatus>(LicenseStatus.Assigned);

  private readonly userId$ = toObservable(this.currentUser.userId);
  private readonly companyId$ = toObservable(this.currentUser.companyId);
  private readonly appSwitcherLoaded$ = toObservable(this.appSwitcher.loaded);

  private readonly destroy$ = new Subject<void>();
  private logoutPollInterval: ReturnType<typeof setInterval> | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.setupLogoutPolling();

    if (this.authService.isAuthenticated()) {
      this.runInitialValidation();
    }
  }

  private setupLogoutPolling(): void {
    this.logoutPollInterval = setInterval(() => {
      if (this.authService.hasLogoutBroadcast()) {
        this.authService.clearLogoutBroadcast();
        this.authService.performLogout();
      }
    }, 3000);
  }

  private runInitialValidation(): void {
    this.licenseStatus.set(LicenseStatus.Unknown);
    this.authService.clearLogoutBroadcast();
    this.appSwitcher.loadApps();

    combineLatest([this.userId$.pipe(filter(val => !!val)), this.companyId$.pipe(filter(val => !!val))])
      .pipe(
        take(1),
        tap(() => {
          /* User context ready */
        }),

        // 1. License Management Sequence
        concatMap(([userId, companyId]) => {
          const isInitialized = sessionStorage.getItem(STORAGE_KEYS.LICENSE_INITIALIZED);
          if (isInitialized) {
            return of(undefined);
          }
          return this.handleLicenseSequence(Number(userId), Number(companyId));
        }),

        // 2. Wait for Store Context
        concatMap(() =>
          this.appSwitcherLoaded$.pipe(
            filter(loaded => loaded),
            take(1)
          )
        ),

        // 3. Store Configuration Validation
        concatMap(() => {
          return this.newDonationSvc.validateConfig().pipe(
            catchError(err => {
              console.error('App: Config validation failed', err);
              return of({ isAcceptDonations: true }); // Fallback to avoid lock if API fails
            })
          );
        }),

        take(1)
      )
      .subscribe({
        next: config => {
          if (!config.isAcceptDonations) {
            this.licenseStatus.set(LicenseStatus.StoreDisabled);
          } else {
            this.licenseStatus.set(LicenseStatus.Assigned);
          }
          this.startHeartbeatPolling();
        },
        error: err => {
          console.error('App: Global initialization error', err);
          // Ensure we don't stay in 'Unknown' state on error
          if (this.licenseStatus() === LicenseStatus.Unknown) {
            this.licenseStatus.set(LicenseStatus.Assigned);
            this.startHeartbeatPolling();
          }
        }
      });
  }

  private startHeartbeatPolling(): void {
    this.stopHeartbeatPolling();

    const companyId = this.currentUser.companyId();
    const sessionId = this.authService.getSessionId();
    if (!companyId || !sessionId) {
      return;
    }

    let consecutiveFailures = 0;
    const maxConsecutiveFailures = 3;

    // Send heartbeat every 3 minutes (180,000 ms)
    this.heartbeatInterval = setInterval(() => {
      const currentSessionId = this.authService.getSessionId();
      const currentCompanyId = this.currentUser.companyId();
      if (!currentSessionId || !currentCompanyId || !this.authService.isAuthenticated()) {
        this.stopHeartbeatPolling();
        return;
      }

      this.companyApi
        .sendHeartbeat(currentCompanyId, {
          appId: Apps.Donation,
          sessionId: currentSessionId
        })
        .subscribe({
          next: () => {
            consecutiveFailures = 0;
            // Heartbeat acknowledged
          },
          error: err => {
            consecutiveFailures++;
            console.warn(
              `App: License heartbeat failed (attempt ${consecutiveFailures}/${maxConsecutiveFailures})`,
              err
            );
            if (consecutiveFailures >= maxConsecutiveFailures || err.status === 401) {
              this.stopHeartbeatPolling();
              this.licenseStatus.set(LicenseStatus.NotFound);
              this.authService.performLogout();
            }
          }
        });
    }, 180000);
  }

  private stopHeartbeatPolling(): void {
    if (this.heartbeatInterval !== null) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private handleLicenseSequence(userId: number, companyId: number): Observable<void> {
    const sessionId = this.authService.getSessionId() ?? undefined;
    return this.companyApi
      .unassignStoreLicense({
        companyId,
        appId: Apps.Donation,
        assignedToUserId: userId,
        assignedToId: 0,
        sessionId
      })
      .pipe(
        catchError(() => of(null)), // Ignore unassign failures
        concatMap(() =>
          this.companyApi.assignStoreLicense({
            companyId,
            appId: Apps.Donation,
            assignedToUserId: userId,
            sessionId
          })
        ),
        tap(res => {
          if (res.status === 4) {
            this.licenseStatus.set(LicenseStatus.NotFound);
            throw new Error('License not found (Status 4)');
          }
        }),
        tap(() => {
          sessionStorage.setItem(STORAGE_KEYS.LICENSE_INITIALIZED, 'true');
        }),
        catchError(err => {
          console.error('App: License assignment failed', err);
          // Check for HTTP 404, application-level status 4 (NotFound) in error body, or the custom error from tap above
          if (err.status === 404 || err.error?.status === 4 || err.message === 'License not found (Status 4)') {
            this.licenseStatus.set(LicenseStatus.NotFound);
          } else {
            // For other errors, we might still want to block or handle differently.
            // For now, following the user request to prioritize status 4 handling.
            this.licenseStatus.set(LicenseStatus.NotFound);
          }
          return throwError(() => err);
        }),
        map(() => undefined)
      );
  }

  onRetry(): void {
    this.stopHeartbeatPolling();
    this.authService.performLogout();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.logoutPollInterval !== null) {
      clearInterval(this.logoutPollInterval);
    }
    this.stopHeartbeatPolling();
  }
}
